import { WorkTemplateTaskModel } from "../../../../../factories/templates/models/template.model";
import { dueDaysToInput, parseDueDays } from "../../../../../utils/dueDays";

export type LocalTemplateSubtask = {
  id: string;
  name: string;
  description: string;
  budgetedTime: string;
  /** "Due: N days" ("" = the work's due date). Roadmap 1849. */
  dueDays: string;
  /** true = after the work's start date, false = before its due date. */
  dueAfterStart: boolean;
  assignedUserId?: string;
  assignedUser?: WorkTemplateTaskModel["assignedUser"];
  defaultTemplateTaskAutomation?: WorkTemplateTaskModel["defaultTemplateTaskAutomation"];
  isNew?: boolean;
  /** Automation picked before the subtask was saved; created on Save. */
  pendingAutomation?: PendingTemplateAutomation;
};

export type PendingTemplateAutomation = {
  conditionId: string;
  actionId: string;
  conditionKey?: string;
  actionKey?: string;
  conditionValue: unknown;
  actionValue: unknown;
  noOfDays?: number;
};

export const getSubtaskBudgetValue = (
  subtask:
    | Pick<WorkTemplateTaskModel, "budgetedTime" | "timeBudget">
    | LocalTemplateSubtask,
) => {
  if ("timeBudget" in subtask && subtask.timeBudget !== undefined) {
    return String(subtask.timeBudget ?? 0);
  }

  if ("budgetedTime" in subtask && subtask.budgetedTime !== undefined) {
    return String(subtask.budgetedTime ?? 0);
  }

  return "0";
};

export const getTimeBudgetValue = (budget: string) => Number(budget || 0);

export const getTemplateTaskBudgetValue = (subtask: WorkTemplateTaskModel) =>
  subtask.timeBudget ?? subtask.budgetedTime ?? 0;

export const mapTemplateTaskToLocalSubtask = (
  subtask: WorkTemplateTaskModel,
): LocalTemplateSubtask => ({
  id: subtask.id,
  name: subtask.name,
  description: subtask.description,
  budgetedTime: getSubtaskBudgetValue(subtask),
  dueDays: dueDaysToInput(subtask.noOfDays),
  // Old rows without a value count as "after start" (the new default).
  dueAfterStart:
    subtask.noOfDays === null || subtask.noOfDays === undefined
      ? true
      : subtask.isAfterStartDate !== false,
  assignedUserId: subtask.assignedUserId,
  assignedUser: subtask.assignedUser,
  defaultTemplateTaskAutomation: subtask.defaultTemplateTaskAutomation,
  isNew: false,
});

export const isSubtaskChanged = (
  original: WorkTemplateTaskModel,
  current: LocalTemplateSubtask,
) =>
  original.name !== current.name ||
  (original.description || "") !== (current.description || "") ||
  (original.assignedUserId || "") !== (current.assignedUserId || "") ||
  getTemplateTaskBudgetValue(original) !==
    getTimeBudgetValue(current.budgetedTime) ||
  isSubtaskDueChanged(original, current);

/** Saved due fields for a subtask row (invalid text = no change sent; Save refuses it first). */
export const getSubtaskDuePayload = (
  subtask: Pick<LocalTemplateSubtask, "dueDays" | "dueAfterStart">,
) => {
  const parsed = parseDueDays(subtask.dueDays);
  const noOfDays = parsed.ok ? parsed.value : null;

  return {
    noOfDays,
    isAfterStartDate: noOfDays === null ? true : subtask.dueAfterStart,
  };
};

export const isSubtaskDueChanged = (
  original: WorkTemplateTaskModel,
  current: LocalTemplateSubtask,
) => {
  const next = getSubtaskDuePayload(current);
  const before = original.noOfDays ?? null;

  if (before !== next.noOfDays) {
    return true;
  }

  return (
    before !== null &&
    (original.isAfterStartDate !== false) !== next.isAfterStartDate
  );
};

/** First subtask whose "Due" days aren't 0..365, or undefined. */
export const findInvalidSubtaskDue = (subtasks: LocalTemplateSubtask[]) =>
  subtasks.find((subtask) => !parseDueDays(subtask.dueDays).ok);

export const reorderItems = <T>(
  list: T[],
  startIndex: number,
  endIndex: number,
) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);

  if (!removed) {
    return result;
  }

  result.splice(endIndex, 0, removed);

  return result;
};

export const createDraftSubtaskId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

// Same limit as the database / server (WorkTemplateTask.Name is 100 characters).
export const SUBTASK_TITLE_MAX = 100;

/** Why a subtask title can't be saved, or "" when it can. */
export const subtaskTitleProblem = (name: string | null | undefined) => {
  const clean = (name || "").trim();
  if (!clean) return "Subtask title is required";
  if (clean.length > SUBTASK_TITLE_MAX)
    return `Subtask titles can be at most ${SUBTASK_TITLE_MAX} characters`;
  return "";
};

/** First subtask whose title can't be saved, or undefined. */
export const findInvalidSubtaskTitle = (subtasks: LocalTemplateSubtask[]) =>
  subtasks.find((subtask) => !!subtaskTitleProblem(subtask.name));

/**
 * The saved ids in the order shown on screen, after new subtasks were added:
 * new rows use the id the server gave them (`createdIds`: local id -> saved
 * id); new rows that failed to save are left out.
 */
export const shownSubtaskOrder = (
  subtasks: Pick<LocalTemplateSubtask, "id" | "isNew">[],
  createdIds: Map<string, string>,
) =>
  subtasks
    .map((subtask) => (subtask.isNew ? createdIds.get(subtask.id) : subtask.id))
    .filter((id): id is string => !!id);

/** "Subtask titles that weren't saved: "A", "B"" for the failure message. */
export const failedSubtasksText = (names: string[]) => {
  const shown = names
    .slice(0, 3)
    .map((name) => `"${name.length > 40 ? `${name.slice(0, 40)}…` : name}"`);
  const more = names.length > 3 ? ` and ${names.length - 3} more` : "";
  return `${
    names.length === 1 ? "This subtask was" : "These subtasks were"
  } not saved: ${shown.join(", ")}${more}.`;
};
